<script setup lang="ts">
// Quote Summary card for the single-scheme sidebar; shares the summary composable with the docked SummaryBar.
import { ref } from 'vue';
import { useSingleSchemeSummary } from './useSingleSchemeSummary';
import WhyInsure from './WhyInsure.vue';
import PdfIcon from '@/components/Common/PdfIcon.vue';

defineEmits<{ (e: 'dock'): void; (e: 'primary'): void; (e: 'email'): void }>();

// Read-only drops the dock control (details views have no journey chrome to dock into).
// primaryLabel drives the sidebar CTA; empty hides it (review / referred / declined).
// `compact` (the in-main second summary): drop the cover breakdown + "Why insure" — the covers sit right above it.
const props = defineProps<{ readonly?: boolean; primaryLabel?: string; primaryDisabled?: boolean; title?: string; compact?: boolean; hideWhy?: boolean; showEmail?: boolean }>();

const { price, priceDisplaySigned, isMonthly, coverMonthly, setMonthly, isMta, coverPremium, total, monthly, plan, iptRate, quoteRef, selectedCovers, documents, docName } = useSingleSchemeSummary();
const coversOpen = ref(true);
const docsOpen = ref(true);
const coverPrice = (c: any) => coverPremium(c)?.TotalPayable ?? null;
</script>

<template>
    <div class="card">
        <div class="card-h" v-if="!compact">
            <h4>{{ props.title ?? 'Quote Summary' }}</h4>
        </div>
        <div class="card-b">
            <div class="pref" v-if="!compact">Quotation reference: <b>{{ quoteRef }}</b></div>

            <!-- Monthly / Annual toggle (MD .payMontlyAnnually) — switches every price below. Hidden on an
                 MTA: the amendment follows the policy's existing payment frequency, and on the compact
                 main-card recap (the toggle lives in the sidebar / bottom dock, not the recap). -->
            <div class="pay-toggle" v-if="monthly && !isMta && !compact">
                <button type="button" class="pt-btn" :class="{ on: isMonthly }" @click="setMonthly(true)">Monthly</button>
                <button type="button" class="pt-btn" :class="{ on: !isMonthly }" @click="setMonthly(false)">Annual</button>
            </div>

            <template v-if="!compact">
                <div class="acc-sub" :class="{ open: coversOpen }" @click="coversOpen = !coversOpen">
                    <span>Cover breakdown</span>
                    <em class="acc-chev far fa-chevron-down"></em>
                </div>
                <ul class="sel-covers" v-show="coversOpen">
                    <li v-for="cover in selectedCovers" :key="cover.CoverId">
                        <span class="nm">{{ cover.CoverName }}</span><span>{{ priceDisplaySigned(coverPrice(cover)) }}</span>
                    </li>
                    <li v-if="isMta && coverMonthly" class="tot">
                        <span class="nm">Monthly Adjustment</span><span>{{ priceDisplaySigned(total) }}</span>
                    </li>
                </ul>
            </template>

            <!-- Premium (MD .summary-total-premium). MTA: the net pro-rata adjustment (to pay or refund).
                 New business: annual = one figure; monthly = deposit + instalments. -->
            <div class="tot-block">
                <template v-if="isMta">
                    <div class="tot-line"><span>{{ total < 0 ? 'Refund due' : 'Premium adjustment' }}</span><span>{{ price(Math.abs(total)) }}</span></div>
                    <template v-if="isMonthly && plan">
                        <div class="tot-line dep"><span>Previous monthly payment</span><span>{{ price(plan.prevInstalment) }}</span></div>
                        <div class="tot-line dep"><span>New monthly payment</span><span>{{ price(plan.instalment) }}</span></div>
                        <div class="tot-line dep"><span>Payments remaining</span><span>{{ plan.installments }}</span></div>
                    </template>
                </template>
                <template v-else-if="!isMonthly">
                    <div class="tot-line"><span>Annual premium</span><span>{{ price(total) }}</span></div>
                </template>
                <template v-else-if="plan">
                    <div class="tot-line dep" v-if="plan.deposit > 0"><span>Deposit</span><span>{{ price(plan.deposit) }}</span></div>
                    <div class="tot-line"><span>then {{ plan.installments }} monthly payments of</span><span>{{ price(plan.instalment) }}</span></div>
                </template>
            </div>

            <p class="pay-note" v-if="isMta">
                (Includes IPT at {{ Number(iptRate || 0).toFixed(1) }}%.) {{ total < 0 ? 'This pro-rata refund covers the remainder of your policy term.' : 'This is the pro-rata cost of your amendment for the remainder of your policy term.' }}
            </p>
            <p class="pay-note" v-else-if="!isMonthly">
                (Includes IPT at {{ Number(iptRate || 0).toFixed(1) }}%.) Spread the cost of your policy for free — switch to monthly payments.
            </p>
            <p class="pay-note" v-else-if="plan">
                Total amount payable {{ price(plan.total || total) }} (including IPT at {{ Number(iptRate || 0).toFixed(1) }}%, {{ plan.apr }}% APR). Your insurance policy will last for 12 months, taken in {{ plan.installments }} monthly payments.
            </p>

            <!-- Primary CTA in the sidebar (MD): Confirm & continue, or Buy now on a confirmed quote. -->
            <div v-if="!props.readonly && props.primaryLabel" class="ssq-summary-cta">
                <button type="button" class="btn pri" :disabled="props.primaryDisabled" @click="$emit('primary')">{{ props.primaryLabel }}</button>
                <button v-if="props.showEmail" type="button" class="btn out ssq-summary-email" @click="$emit('email')">Email quote</button>
                <p class="ssq-act-note">You will be able to review all details before paying. Your quotation is valid for 30 days.</p>
            </div>

            <template v-if="!compact && !readonly && documents.length">
                <div class="acc-sub ssq-docs-head" :class="{ open: docsOpen }" @click="docsOpen = !docsOpen">
                    <span>Documents</span>
                    <em class="acc-chev far fa-chevron-down"></em>
                </div>
                <ul class="sel-docs" v-show="docsOpen">
                    <li v-for="(doc, i) in documents" :key="i"><PdfIcon /><a :href="doc.link" target="_blank" rel="noopener">{{ docName(doc.name) }}</a></li>
                </ul>
            </template>

            <WhyInsure v-if="!compact && !hideWhy" />
        </div>
    </div>
</template>
